import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, inject, Input, NgZone, OnInit, Output } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import 'bingmaps';
import { of, Subject } from 'rxjs';
import { startWith, take } from 'rxjs/operators';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { Tools } from '../../../core/utils/tools';
import { CountryModel } from '../../../models/country.model';
import { LocationModel } from '../../../models/location.model';
import { BingApiLoaderService } from '../../../services/bing-api-loader.service';
import { LocationsResource } from '../../../services/resources/locations.resource';
import { MetaResource } from '../../../services/resources/meta.resource';
import { AddressModel } from './models/address.model';

@Component({
    selector: 'sl-address',
    templateUrl: './address.component.html',
    styleUrls: ['./address.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddressComponent extends BaseComponent implements OnInit, AfterViewInit {
    @Input() formGroup: UntypedFormGroup;
    @Input() defaultCountryId = 178;
    @Input() address = new AddressModel();
    @Input() isValid: boolean;
    @Input() isDisabled = false;
    @Output() readonly addressChange = new EventEmitter<AddressModel>();
    @Output() readonly isValidChange = new EventEmitter<boolean>();
    @Output() readonly locationSelected = new EventEmitter<string>();
    @Output() readonly anyFieldBlur = new EventEmitter<void>();

    countries: CountryModel[] = [];
    locations: LocationModel[] = [];
    filteredCountries: CountryModel[] = [];
    filteredLocations: LocationModel[] = [];
    countryControl = new UntypedFormControl();
    cityControl = new UntypedFormControl();
    shouldAddLocation = false;

    private locationsSubject = new Subject<boolean>();
    private isLoadingLocations = false;
    private previousLocationId = 0;
    private lastSelectedCountry = 0;
    private lastSelectedZipCode = '';
    private lastSelectedCity = '';
    private readonly ngZone = inject(NgZone);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly metaResource = inject(MetaResource);
    private readonly locationsResource = inject(LocationsResource);
    private readonly bingApiLoaderService = inject(BingApiLoaderService);

    ngOnInit(): void {
        const controlValue = this.isDisabled ? { value: '', disabled: true } : undefined;
        this.formGroup =
            this.formGroup
            || this.formBuilder.group({
                street: new FormControl(controlValue, undefined),
                streetNumber: new FormControl(controlValue, undefined),
                streetSupplement: new FormControl(controlValue, undefined),
                streetAfterSupplement: new FormControl(controlValue, undefined),
                isPoBox: new FormControl(controlValue, undefined),
                poBox: new FormControl(controlValue, undefined),
                locationZipCode: new FormControl(controlValue, undefined),
                locationCity: new FormControl(controlValue, undefined),
                countryId: new FormControl(controlValue, undefined),
                locationId: new FormControl(controlValue, undefined)
            });

        if (!!this.address) {
            this.formGroup.patchValue(this.address);
        }

        if (this.isDisabled) {
            this.cityControl.disable();
            this.countryControl.disable();
        }

        const valChanges = this.formGroup.valueChanges.subscribe(v => {
            if (v.locationId && v.locationId !== this.previousLocationId) {
                this.previousLocationId = v.locationId;
                this.loadFromLocationId();
            }

            if (!!v.countryId) {
                const defaultCountry = this.countries.find(c => c.id === v.countryId);
                if (!!defaultCountry) {
                    this.countryControl.setValue(defaultCountry.name);
                }
            }

            this.addressChange.emit(v);
        });
        const statusChanges = this.formGroup.statusChanges.subscribe(s => {
            if (!!this.formGroup.controls['locationCity'].errors) {
                this.cityControl.setErrors(this.formGroup.controls['locationCity'].errors);
            }

            if (!!this.formGroup.controls['countryId'].errors) {
                this.countryControl.setErrors(this.formGroup.controls['countryId'].errors);
            }

            this.isValidChange.emit(s === 'VALID');
        });
        const cityValChanges = this.cityControl.valueChanges
            .pipe(Tools.RxJs.autocomplete(200, term => of(this.resetLocations(!!term && term.length > 0))))
            .subscribe();

        this.metaResource.getCountries().subscribe(r => {
            this.countries = r.value;

            if (!!this.defaultCountryId) {
                const defaultCountry = this.countries.find(c => +c.id === this.defaultCountryId);

                if (!!defaultCountry) {
                    this.countryControl.setValue(defaultCountry.name);
                    this.selectCountry(defaultCountry);
                }
            }

            const valCountryChanges = this.countryControl.valueChanges
                .pipe(startWith(''))
                .subscribe(
                    (query: string) =>
                        (this.filteredCountries =
                            query && query.length > 0
                                ? this.countries.filter(c => c.name.toLowerCase().includes(query.toLowerCase()))
                                : this.countries)
                );

            this.addSubscriptions(valCountryChanges);
        });

        this.addSubscriptions(valChanges, statusChanges, cityValChanges);
    }

    ngAfterViewInit(): void {
        this.bingApiLoaderService.onMapsApiLoaded.pipe(take(1)).subscribe(() => this.setupMapsSuggestion());
        this.bingApiLoaderService.loadMaps();
    }

    countryBlur(): void {
        if (!!this.countryControl.value) {
            const selectedCountry = this.filteredCountries.find(c => c.name.toLowerCase() === this.countryControl.value.toLowerCase());

            if (!!selectedCountry) {
                this.selectCountry(selectedCountry);
            }
        }

        if (!!this.formGroup.value.country) {
            this.anyFieldBlur.emit();
            this.resetLocations(true);
        }
    }

    selectCountry(country: CountryModel): void {
        const countryId = country.id;
        const previousCountryId = this.formGroup.controls['countryId'].value;

        if (previousCountryId && countryId !== previousCountryId) {
            this.formGroup.controls['locationZipCode'].setValue(undefined);
            this.clearCity();

            this.filteredLocations = [];
        }

        this.formGroup.controls['countryId'].setValue(countryId);
        this.resetLocations(true);
        this.onAnyFieldBlur();
    }

    clearCountry(): void {
        this.formGroup.controls['countryId'].setValue(undefined);
        this.formGroup.controls['locationZipCode'].setValue(undefined);
        this.countryControl.setValue(undefined);
        this.clearCity();
        this.filteredLocations = [];
    }

    selectLocation(locationId: string): void {
        this.shouldAddLocation = false;
        const location = this.locations.find(c => c.id === locationId);

        if (!location) {
            return;
        }

        this.formGroup.controls['locationId'].setValue(locationId);
        this.formGroup.controls['locationCity'].setValue(location.city);
        this.formGroup.controls['locationZipCode'].setValue(location.zipCode);
        this.locationSelected.emit(locationId);
        this.onAnyFieldBlur();
    }

    clearCity(): void {
        if (!this.isLoadingLocations) {
            this.execClearCity();
        } else {
            const lSub = this.locationsSubject.subscribe(isLoading => {
                if (!isLoading) {
                    this.execClearCity();
                    lSub.unsubscribe();
                }
            });
            this.addSubscriptions(lSub);
        }
    }

    cityFieldBlur(): void {
        if (!!this.cityControl.value) {
            this.resetLocations(true);
        }
    }

    addNewLocation(): void {
        if (!!this.formGroup.value.locationCity) {
            const newLocation = new LocationModel({
                countryId: this.formGroup.value.countryId,
                city: this.cityControl.value,
                zipCode: this.formGroup.value.locationZipCode
            });

            this.locationsResource.add(newLocation).subscribe(r => {
                this.shouldAddLocation = false;
                this.formGroup.controls['locationId'].setValue(r.value.result);
                this.locationSelected.emit(r.value.result.toString());
                this.onAnyFieldBlur();
            });
        }
    }

    onAnyFieldBlur(): void {
        this.anyFieldBlur.emit();
    }

    zipCodeBlur(): void {
        if (!!this.formGroup.value.locationZipCode) {
            this.anyFieldBlur.emit();
            this.resetLocations(true);
        }
    }

    private resetLocations(autoSelectCity: boolean): void {
        const countryId = this.formGroup.value.countryId;
        const zipCode = this.formGroup.value.locationZipCode;
        const city = this.cityControl.value;

        if (!countryId || (!zipCode && !city)) {
            this.filteredLocations = [];
            return;
        }

        if (this.lastSelectedCountry === countryId && this.lastSelectedZipCode === zipCode && this.lastSelectedCity === city) {
            return;
        }

        this.lastSelectedCountry = countryId;
        this.lastSelectedZipCode = zipCode;
        this.lastSelectedCity = city;
        this.isLoadingLocations = true;

        this.locationsSubject.next(true);
        this.locationsResource.getAll(countryId, city, zipCode).subscribe(r => {
            this.locations = r.value;
            this.filteredLocations =
                city && city.length > 0 ? this.locations.filter(c => c.city.toLowerCase().includes(city.toLowerCase())) : this.locations;

            if (autoSelectCity && this.filteredLocations.length === 1) {
                const selectedLocation = this.filteredLocations[0];

                this.selectLocation(selectedLocation.id);
                this.cityControl.setValue(selectedLocation.city);
            }

            this.shouldAddLocation = city && this.locations.length < 1;
            this.isLoadingLocations = false;

            this.locationsSubject.next(false);
        });
    }

    private loadFromLocationId(): void {
        this.locationsResource.get(this.previousLocationId.toString()).subscribe(r => {
            this.formGroup.controls['countryId'].setValue(r.value.countryId);
            this.formGroup.controls['locationZipCode'].setValue(r.value.zipCode);
            this.cityControl.setValue(r.value.city);
            this.formGroup.controls['locationCity'].setValue(r.value.city);
        });
    }

    private execClearCity(): void {
        this.shouldAddLocation = false;

        this.cityControl.setValue(undefined);
        this.formGroup.controls['locationCity'].setValue(undefined);
        this.onAnyFieldBlur();
    }

    private setupMapsSuggestion(): void {
        this.bingApiLoaderService.addAutoSuggest('streetField', 'searchResults', this.handleSuggestions.bind(this));
    }

    private handleSuggestions(result: Microsoft.Maps.ISuggestionResult): void {
        const streetParts = result.address.addressLine.match(/^(.*?)(\d+)(\D*)$/);
        const street = streetParts ? streetParts[1] : result.address.addressLine;
        const streetNumber = streetParts && streetParts.length > 2 ? streetParts[2] : '';

        this.ngZone.run(() => {
            // This is required because bing's AutoSuggest API changes the value of the input with the full search
            // result. And it does so after this method is executed. Because of that, we need to wait 100ms for bing
            // to update the input, so we can re-update it with the correct value. I couldn't find in the docs any option
            // to remove that behavior. Once we can figure that one out, we should remove this setTimeout.
            setTimeout(() => this.formGroup.controls['street'].setValue(street), 100);

            this.formGroup.controls['streetNumber'].setValue(streetNumber);
            this.formGroup.controls['locationZipCode'].setValue(result.address.postalCode);
            this.cityControl.setValue(result.address.locality);
            this.formGroup.controls['locationCity'].setValue(result.address.locality);
        });
    }
}
